import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import PlatformDisplayUserInformation from "./PlatformDisplayUserInformation";
import PlatformUserInformation from "./PlatformUserInformation";
import IdNotFoundPage from "../../common/IdNotFoundPage";
import useUserProfileData from "hooks/useUserProfileData";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { Drawer } from "@outpave/ds";

interface UserProfileDrawerProps {
  selectedUserId?: string;
  open: boolean;
  edit?: boolean;
  isReactivateUser?: boolean;
  handleCloseDrawer: () => void;
  handleUpdateUser?: (error?: string) => void;
}

const PlatformUserProfileDrawer: React.FC<UserProfileDrawerProps> = (props) => {
  const {
    selectedUserId,
    open,
    edit = false,
    isReactivateUser = false,
    handleCloseDrawer,
    handleUpdateUser,
  } = props;

  const { t } = useTranslation();

  const [isEditable, setIsEditable] = useState<boolean>(edit);
  const [reactivateUser, setReactivateUser] =
    useState<boolean>(isReactivateUser);

  useEffect(() => {
    setReactivateUser(isReactivateUser);
  }, [isReactivateUser]);

  const { formikAddUser, customFieldSettings, specificLoadingStates } =
    useUserProfileData({
      selectedUserId,
      reactivateUserAfterEdit: Boolean(reactivateUser),
      handleUpdateUser,
      setIsEditable,
      setReactivateUser,
      isPlatform: true,
    });

  const isLoadingUsers =
    specificLoadingStates.loadingUsers && !formikAddUser.values.id;

  // Drives both the drawer overlay and the in-body fields so they stay in
  // sync (e.g. the account autocomplete is disabled instead of running its
  // own async spinner while the record loads/saves).
  const isDrawerLoading =
    specificLoadingStates.loadingUsers ||
    specificLoadingStates.isUpdateUser ||
    specificLoadingStates.reactivatingUser;

  const { validateAndTouch } = useFormikHelpers(formikAddUser);

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    formikAddUser.dirty,
    () => {
      setIsEditable(false);
      handleCloseDrawer();
    },
  );

  /* handles closing the drawer */
  const handleClose = () => {
    if (isDrawerLoading) return;
    requestLeave();
  };

  /* Opens the edit drawer */
  const handleClickEdit = () => {
    setIsEditable(true);
  };

  useEffect(() => {
    setIsEditable(edit);
  }, [edit]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={[t("User Profile")]}
      loading={isDrawerLoading}
      footer={
        isEditable ? (
          <>
            <Button
              variant="primary-outlined"
              onClick={() => {
                handleClose();
              }}
            >
              {t("Cancel")}
            </Button>

            <Button
              variant="primary-filled"
              type="submit"
              onClick={async () => {
                const isValid = await validateAndTouch();
                if (isValid) {
                  formikAddUser.handleSubmit();
                }
              }}
            >
              {reactivateUser ? t("Reactivate") : t("Save")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="default-outlined" onClick={handleCloseDrawer}>
              {t("Back")}
            </Button>
          </>
        )
      }
    >
      <>
        <>
          {!isEditable && (
            <>
              {isLoadingUsers || !formikAddUser.values?.id ? (
                <IdNotFoundPage
                  title={"User"}
                  isLoading={isLoadingUsers}
                  showSkeleton={true}
                />
              ) : (
                <PlatformDisplayUserInformation
                  handleClickEdit={handleClickEdit}
                  formik={formikAddUser}
                  handleUpdateUser={handleUpdateUser}
                  handleClickReactivateUser={() => {
                    setIsEditable(true);
                    setReactivateUser(true);
                  }}
                />
              )}
            </>
          )}

          {isEditable && (
            <>
              <PlatformUserInformation
                formik={formikAddUser}
                customFieldSettings={customFieldSettings}
                loading={isDrawerLoading}
              />
            </>
          )}
        </>

        {dialog}
      </>
    </Drawer>
  );
};

export default PlatformUserProfileDrawer;
